import { openDB } from 'idb';

/**
 * IndexedDB local-first mirror of the user's practice sessions
 * (Firestore `users/{uid}/sessionHistory`). Lets login render the full history
 * instantly from disk (zero network reads) while `sessionSync.js` fetches only
 * the delta since last login.
 *
 * Stores:
 *   sessions  (keyPath ['uid','id']) — one row per session doc
 *     {
 *       uid, id,
 *       data        : the full session payload the app consumes (JSON-plain),
 *       updatedAtMs : server updatedAt in ms; null while provisional / un-acked,
 *       deleted     : tombstone flag,
 *       provisional : true for a local write not yet confirmed by a server read
 *     }
 *   meta      (keyPath 'uid')
 *     { uid, cursor: {seconds,nanoseconds}|null, fullPulledAt: ms|null }
 *
 * The compound [uid,id] key keeps multiple accounts on one device isolated
 * WITHOUT a secondary index (the codebase uses none). One user's rows are listed
 * with a key range over the `[uid, *]` prefix — `[]` sorts after any string/number
 * key, so `[uid] .. [uid, []]` bounds exactly that user's rows.
 *
 * Follows the same module-level `openDB` singleton pattern as uploadPersistence.js.
 */

const DB_NAME = 'ff-session-mirror';
const DB_VERSION = 1;
const SESSIONS = 'sessions';
const META = 'meta';

let dbPromise = null;
function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(SESSIONS)) {
          db.createObjectStore(SESSIONS, { keyPath: ['uid', 'id'] });
        }
        if (!db.objectStoreNames.contains(META)) {
          db.createObjectStore(META, { keyPath: 'uid' });
        }
      },
    });
  }
  return dbPromise;
}

function uidRange(uid) {
  // All keys [uid, *]. The empty-array upper bound sorts after every string/number id.
  return IDBKeyRange.bound([uid], [uid, []]);
}

// All rows for a user (live + tombstones + provisional). Callers filter.
export async function getAllRows(uid) {
  if (!uid) return [];
  const db = await getDb();
  return db.getAll(SESSIONS, uidRange(uid));
}

export async function getMeta(uid) {
  const db = await getDb();
  return (await db.get(META, uid)) || { uid, cursor: null, fullPulledAt: null };
}

/**
 * Write-through for a local save/delete. Marked `provisional` so it renders
 * immediately but NEVER influences the sync cursor until a server read confirms
 * it (a just-written serverTimestamp() reads back null locally, and mixing a
 * client clock into a server-time high-water mark would skip docs).
 */
export async function putProvisional({ uid, id, data, deleted = false }) {
  if (!uid || !id) return;
  const db = await getDb();
  await db.put(SESSIONS, {
    uid,
    id,
    data: data || { id },
    updatedAtMs: null,
    deleted: !!deleted,
    provisional: true,
  });
}

/**
 * Apply a batch of sync results atomically (single transaction over both stores).
 *   ops: Array<{ type:'upsert', row } | { type:'tombstone', id, updatedAtMs }>
 *   advanceCursorTo: a Firestore Timestamp (or null). The cursor only ever moves
 *                    FORWARD (monotonic) — protects against multi-tab regression.
 *   markFullPulled:  stamp fullPulledAt = now (drives the periodic safety-net pull).
 */
export async function applyBatch(uid, ops, { advanceCursorTo = null, markFullPulled = false } = {}) {
  if (!uid) return;
  const db = await getDb();
  const tx = db.transaction([SESSIONS, META], 'readwrite');
  const sStore = tx.objectStore(SESSIONS);
  const mStore = tx.objectStore(META);

  for (const op of ops) {
    if (op.type === 'upsert') {
      await sStore.put(op.row);
    } else if (op.type === 'tombstone') {
      await sStore.put({
        uid,
        id: op.id,
        data: { id: op.id, deleted: true },
        updatedAtMs: op.updatedAtMs ?? null,
        deleted: true,
        provisional: false,
      });
    }
  }

  const meta = (await mStore.get(uid)) || { uid, cursor: null, fullPulledAt: null };
  if (advanceCursorTo) {
    const curMs = meta.cursor
      ? meta.cursor.seconds * 1000 + Math.floor((meta.cursor.nanoseconds || 0) / 1e6)
      : -1;
    const newMs = advanceCursorTo.seconds * 1000 + Math.floor((advanceCursorTo.nanoseconds || 0) / 1e6);
    if (newMs > curMs) {
      meta.cursor = { seconds: advanceCursorTo.seconds, nanoseconds: advanceCursorTo.nanoseconds };
    }
  }
  if (markFullPulled) {
    meta.fullPulledAt = Date.now();
  }
  await mStore.put(meta);
  await tx.done;
}

// Remove a single user's rows + meta. Used on logout / account switch so one
// device's accounts never leak into each other.
export async function clearMirror(uid) {
  if (!uid) return;
  const db = await getDb();
  const tx = db.transaction([SESSIONS, META], 'readwrite');
  const sStore = tx.objectStore(SESSIONS);
  let cursor = await sStore.openCursor(uidRange(uid));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.objectStore(META).delete(uid);
  await tx.done;
}
